/**
 * ═══════════════════════════════════════════════════════════════════
 *  Tour DOM Utilities
 *  Find elements, calculate positions, scroll into view
 * ═══════════════════════════════════════════════════════════════════
 */

/**
 * Find a DOM element matching the given CSS selector.
 * Retries up to `maxRetries` times with a delay (for elements that
 * render asynchronously).
 */
export function findTargetElement(selector, maxRetries = 5, delay = 200) {
  return new Promise((resolve) => {
    let attempts = 0;
    const tryFind = () => {
      const el = document.querySelector(selector);
      if (el) return resolve(el);
      attempts++;
      if (attempts >= maxRetries) return resolve(null);
      setTimeout(tryFind, delay);
    };
    tryFind();
  });
}

/**
 * Synchronous version — returns element or null immediately.
 */
export function findTargetSync(selector) {
  if (!selector) return null;
  return document.querySelector(selector);
}

/**
 * Get the bounding rect of an element relative to the viewport,
 * with optional padding.
 */
export function getElementRect(element, padding = 8) {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top - padding,
    left: rect.left - padding,
    right: rect.right + padding,
    bottom: rect.bottom + padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
    // Original unpadded values
    rawTop: rect.top,
    rawLeft: rect.left,
    rawWidth: rect.width,
    rawHeight: rect.height,
  };
}

/**
 * Find the nearest scrollable ancestor of an element.
 */
function findScrollableParent(element) {
  let current = element?.parentElement;
  while (current) {
    const style = window.getComputedStyle(current);
    const overflowY = style.overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * Scroll an element into view smoothly, centered vertically.
 * Works with both window scroll and scrollable ancestor containers.
 */
export function scrollToElement(element, offset = 80) {
  if (!element) return;
  const rect = element.getBoundingClientRect();

  // Check if already in viewport
  const isInView =
    rect.top >= offset &&
    rect.bottom <= window.innerHeight - 20;
  if (isInView) return;

  // Try scrollable parent first (for overflow:auto containers)
  const scrollable = findScrollableParent(element);
  if (scrollable) {
    const containerRect = scrollable.getBoundingClientRect();
    const relativeTop = rect.top - containerRect.top + scrollable.scrollTop;
    const targetScroll = relativeTop - scrollable.clientHeight / 3;
    scrollable.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' });
    return;
  }

  // Fallback to window scroll
  const scrollTop =
    window.pageYOffset + rect.top - window.innerHeight / 3;
  window.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
}

/**
 * Calculate optimal tooltip placement relative to the target element.
 * Returns { top, left, placement } for absolute positioning.
 */
export function calculateTooltipPosition(targetRect, tooltipWidth = 320, tooltipHeight = 200, preferredPlacement = 'bottom') {
  if (!targetRect) {
    // Center on screen (no target — welcome/completion modals)
    return {
      top: window.innerHeight / 2 - tooltipHeight / 2,
      left: window.innerWidth / 2 - tooltipWidth / 2,
      placement: 'center',
    };
  }

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const gap = 12; // space between target and tooltip
  const edgePad = 16; // minimum distance from viewport edges

  // Try placements in preference order
  const placements = [preferredPlacement, 'bottom', 'top', 'right', 'left'];
  const seen = new Set();

  for (const p of placements) {
    if (seen.has(p)) continue;
    seen.add(p);

    let top, left;

    switch (p) {
      case 'bottom':
        top = targetRect.bottom + gap;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        if (top + tooltipHeight < vh - edgePad && left > edgePad && left + tooltipWidth < vw - edgePad) {
          return { top, left: Math.max(edgePad, Math.min(left, vw - tooltipWidth - edgePad)), placement: 'bottom' };
        }
        break;

      case 'top':
        top = targetRect.top - tooltipHeight - gap;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        if (top > edgePad && left > edgePad && left + tooltipWidth < vw - edgePad) {
          return { top, left: Math.max(edgePad, Math.min(left, vw - tooltipWidth - edgePad)), placement: 'top' };
        }
        break;

      case 'right':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.right + gap;
        if (left + tooltipWidth < vw - edgePad && top > edgePad && top + tooltipHeight < vh - edgePad) {
          return { top: Math.max(edgePad, top), left, placement: 'right' };
        }
        break;

      case 'left':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.left - tooltipWidth - gap;
        if (left > edgePad && top > edgePad && top + tooltipHeight < vh - edgePad) {
          return { top: Math.max(edgePad, top), left, placement: 'left' };
        }
        break;

      default:
        break;
    }
  }

  // Fallback: bottom with clamped position
  return {
    top: Math.min(targetRect.bottom + gap, vh - tooltipHeight - edgePad),
    left: Math.max(edgePad, Math.min(
      targetRect.left + targetRect.width / 2 - tooltipWidth / 2,
      vw - tooltipWidth - edgePad
    )),
    placement: 'bottom',
  };
}

/**
 * Generate a CSS box-shadow string that creates a spotlight cutout.
 * The shadow covers the entire viewport except for the target rect.
 */
export function createSpotlightShadow(targetRect, color = 'rgba(0, 0, 0, 0.55)') {
  if (!targetRect) return `0 0 0 9999px ${color}`;
  // Use the padded values for the spotlight cutout
  return `0 0 0 9999px ${color}`;
}
